import Phaser from 'phaser';
import { COLORS } from '../utils/constants';

export interface TouchState {
  moveX: number;       // -1 to 1
  moveY: number;       // -1 to 1
  attack: boolean;
  heavy: boolean;
  skill: boolean;
  shield: boolean;
  dash: boolean;
}

export class TouchControls {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  public state: TouchState = {
    moveX: 0, moveY: 0,
    attack: false, heavy: false, skill: false,
    shield: false, dash: false,
  };
  private enabled = false;

  // Joystick
  private joystickBase: Phaser.GameObjects.Arc | null = null;
  private joystickThumb: Phaser.GameObjects.Arc | null = null;
  private joystickActive = false;
  private joystickId = -1;
  private jx = 0;
  private jy = 0;
  private maxDist = 60;

  // Action buttons
  private actionButtons: { bg: Phaser.GameObjects.Rectangle; key: string; label: string }[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.container = scene.add.container(0, 0).setDepth(100);
    this.enabled = this.isTouchDevice();
    if (this.enabled) this.createUI();
  }

  private isTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  private createUI() {
    const cam = this.scene.cameras.main;
    const pad = 30;
    const btnSize = 56;
    const alpha = 0.3;

    // Joystick base (fixed position bottom-left)
    this.jx = 120;
    this.jy = cam.height - 140;
    this.joystickBase = this.scene.add.circle(this.jx, this.jy, this.maxDist, COLORS.cyan, alpha * 0.5)
      .setStrokeStyle(2, COLORS.cyan, 0.4).setDepth(100);
    this.joystickThumb = this.scene.add.circle(this.jx, this.jy, 24, COLORS.cyan, alpha * 0.8)
      .setDepth(101);

    // Touch handlers
    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const dist = Phaser.Math.Distance.Between(pointer.x, pointer.y, this.jx, this.jy);
      if (dist < this.maxDist * 1.5) {
        this.joystickActive = true;
        this.joystickId = pointer.id;
        this.updateJoystick(pointer.x, pointer.y);
      }
    });

    this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.joystickActive && pointer.id === this.joystickId) {
        this.updateJoystick(pointer.x, pointer.y);
      }
    });

    this.scene.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (this.joystickActive && pointer.id === this.joystickId) {
        this.joystickActive = false;
        this.joystickId = -1;
        this.state.moveX = 0;
        this.state.moveY = 0;
        this.joystickThumb!.setPosition(this.jx, this.jy);
      }
    });

    // Action buttons (right side)
    const rCol1 = cam.width - pad - btnSize * 1.5;
    const rCol2 = cam.width - pad - btnSize * 0.5;
    const baseY = cam.height - pad - btnSize * 1.5;

    const actions: { x: number; y: number; key: keyof TouchState; label: string }[] = [
      { x: rCol1, y: baseY - btnSize - 10, key: 'dash', label: 'SH' },
      { x: rCol1, y: baseY, key: 'attack', label: 'J' },
      { x: rCol2, y: baseY, key: 'heavy', label: 'K' },
      { x: rCol2, y: baseY - btnSize - 10, key: 'skill', label: 'L' },
      { x: (rCol1 + rCol2) / 2, y: baseY + btnSize + 10, key: 'shield', label: 'SP' },
    ];

    actions.forEach((a) => {
      const bg = this.scene.add.rectangle(a.x, a.y, btnSize, btnSize, COLORS.cyan, alpha)
        .setStrokeStyle(2, COLORS.cyan, 0.4).setDepth(100);
      const txt = this.scene.add.text(a.x, a.y, a.label, {
        fontFamily: 'Arial', fontSize: '16px', color: '#00f0ff', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(101);
      const hitArea = this.scene.add.rectangle(a.x, a.y, btnSize + 10, btnSize + 10, 0x000000, 0)
        .setInteractive({ useHandCursor: true }).setDepth(102);

      const key = a.key;
      hitArea.on('pointerdown', () => {
        (this.state as any)[key] = true;
        bg.setAlpha(0.7);
      });
      hitArea.on('pointerup', () => {
        (this.state as any)[key] = false;
        bg.setAlpha(alpha);
      });
      hitArea.on('pointerout', () => {
        (this.state as any)[key] = false;
        bg.setAlpha(alpha);
      });

      this.actionButtons.push({ bg, key: a.key, label: a.label });
      this.container.add([bg, txt, hitArea]);
    });
  }

  private updateJoystick(px: number, py: number) {
    let dx = px - this.jx;
    let dy = py - this.jy;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > this.maxDist) {
      dx = (dx / dist) * this.maxDist;
      dy = (dy / dist) * this.maxDist;
    }

    this.joystickThumb!.setPosition(this.jx + dx, this.jy + dy);

    // Normalize to -1..1
    this.state.moveX = Phaser.Math.Clamp(dx / this.maxDist, -1, 1);
    this.state.moveY = Phaser.Math.Clamp(dy / this.maxDist, -1, 1);
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  destroy() {
    this.container.destroy();
    if (this.joystickBase) this.joystickBase.destroy();
    if (this.joystickThumb) this.joystickThumb.destroy();
  }
}
